import { useEffect, useRef, useState } from 'react'
import { generateMermaidDiagram } from '../utils/mermaidGenerator'

let diagramInstanceCount = 0

interface ProvenanceDiagramProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  manifestStore: Record<string, any>
  className?: string
}

/**
 * Renders a Mermaid TD (top-down) flowchart showing the C2PA ingredient provenance chain.
 * Styled for the Phenom dark UI theme.
 *
 * - Cyan  = current (active) manifest
 * - Red   = original ingredient source
 * - Green = ingredient with valid C2PA
 * - Red   = ingredient with validation failures
 * - Grey  = ingredient with no C2PA data
 */
export function ProvenanceDiagram({ manifestStore, className = '' }: ProvenanceDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const instanceIdRef = useRef(`prov-${diagramInstanceCount++}`)
  const [status, setStatus] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false

    async function render() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const diagramDef = generateMermaidDiagram(manifestStore as any)

      if (!diagramDef) {
        setStatus('empty')
        return
      }

      try {
        const mermaid = (await import('mermaid')).default

        mermaid.initialize({
          startOnLoad: false,
          theme: 'dark',
          themeVariables: {
            fontSize: '13px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            lineColor: '#6b7280',
            edgeLabelBackground: '#1a1a1a',
            primaryColor: '#a5e3e8',
            primaryTextColor: '#121010',
            primaryBorderColor: '#7ec8cf',
            darkMode: true,
            background: 'transparent',
          },
          flowchart: {
            curve: 'basis',
            useMaxWidth: true,
          },
          securityLevel: 'loose',
        })

        const { svg } = await mermaid.render(instanceIdRef.current, diagramDef)

        if (cancelled) return

        if (containerRef.current) {
          containerRef.current.innerHTML = svg
          const svgEl = containerRef.current.querySelector('svg')
          if (svgEl) {
            svgEl.style.maxWidth = '100%'
            svgEl.style.height = 'auto'
          }
          setStatus('ready')
        }
      } catch (err) {
        console.warn('ProvenanceDiagram render error:', err)
        if (!cancelled) setStatus('error')
      }
    }

    render()
    return () => { cancelled = true }
  }, [manifestStore])

  if (status === 'empty') return null

  return (
    <div className={className}>
      {status === 'loading' && (
        <div className="flex items-center gap-2 text-xs text-phenom-gray p-3">
          <div className="w-3 h-3 border border-phenom-cyan border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span>Rendering provenance diagram...</span>
        </div>
      )}

      {status === 'error' && (
        <div className="text-xs text-phenom-gray/50 italic p-3 text-center">
          Diagram unavailable
        </div>
      )}

      {/* SVG container */}
      <div
        ref={containerRef}
        className="overflow-x-auto"
        style={{ display: status === 'ready' ? 'block' : 'none' }}
      />

      {/* Legend */}
      {status === 'ready' && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-phenom-gray/70">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#a5e3e8]" />
            Current file
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#d73429]" />
            Original source
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />
            Added ingredient
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-red-500" />
            Invalid
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-gray-500" />
            Unverified
          </span>
        </div>
      )}
    </div>
  )
}
